import classnames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { InView } from 'react-intersection-observer';
import { useSelector } from 'react-redux';

import PreferredBreederBadge from 'dpl/breeder_preferred_qualifications/components/PreferredBreederBadge';
import { STATUS } from 'dpl/breeder_preferred_qualifications/utils/constants';
import CardMediaCarousel from 'dpl/breeder_search_results/components/CardMediaCarousel';
import TransportationModal from 'dpl/breeder_search_results/components/TransportationModal';
import { TRANSPORT_TYPES } from 'dpl/breeder_search_results/utils/constants';
import ClickableDiv from 'dpl/common/components/ClickableDiv';
import Icon from 'dpl/common/components/Icon';
import Badge from 'dpl/common/design_system/Badge';
import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEW_FEATURE_LOCATIONS,
  ANALYTICS_VIEW_FEATURES
} from 'dpl/constants/analytics';
import FavoriteButton from 'dpl/favorites/components/FavoriteButton';
import { FOLLOWABLE_TYPES } from 'dpl/favorites/utils/constants';
import UserAvatar from 'dpl/path_to_payment/components/UserAvatar';
import { openPuppyPage } from 'dpl/puppy_page/utils/helpers';
import { PUPPY_GENDERS } from 'dpl/puppy_search_results/utils/constants';
import { GENDERS } from 'dpl/shared/constants/breeder_app';
import { isNumeric } from 'dpl/shared/form/utils';
import { capitalize, flatten } from 'dpl/shared/utils';
import {
  PuppyCardPropDefaultTypes,
  PuppyCardPropTypes
} from 'dpl/shared/utils/customPropTypes';
import { getAnalyticsOrigin } from 'dpl/shared/utils/getAnalyticsOrigin';
import { setAnalyticsOrigin } from 'dpl/shared/utils/setAnalyticsOrigin';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import { isLessThanBreakpoint } from 'dpl/util/grid';

const IS_LT_MD = isLessThanBreakpoint('md');
const { AIR, GROUND } = TRANSPORT_TYPES;

function getPuppyName(puppy) {
  if (puppy.gender === GENDERS.MALE && puppy.name.startsWith('Boy ')) {
    return puppy.name;
  } else if (
    puppy.gender === GENDERS.FEMALE &&
    puppy.name.startsWith('Girl ')
  ) {
    return puppy.name;
  }

  return `${puppy.name}, ${capitalize(
    puppy.gender === GENDERS.MALE ? PUPPY_GENDERS.BOY : PUPPY_GENDERS.GIRL
  )}`;
}

function Transport({
  deliveryMethods,
  breederFirstName,
  facilityLocation,
  puppyName,
  distance
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const transportTypes = Object.keys(deliveryMethods);

  const hasTransportation =
    transportTypes.includes(AIR) || transportTypes.includes(GROUND);

  function handleOpenClick(e) {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  }

  function handleCloseClick(e) {
    e?.stopPropagation?.();
    setIsModalOpen(false);
  }

  const mileOrMiles = distance === 1 ? 'mile' : 'miles';

  const milesAway = isNumeric(distance)
    ? `${distance} ${mileOrMiles} away`
    : '';

  const isNearby = distance <= 200;

  return (
    <div className="PuppySearchResultsTransport font-12 tertiary">
      {isNearby && milesAway ? (
        <p>{milesAway}</p>
      ) : (
        <button
          type="button"
          className="flex items-center pointer underline"
          tabIndex="0"
          onClick={handleOpenClick}
        >
          {hasTransportation ? 'Drop off to you' : 'Meet breeder for pickup'}
        </button>
      )}
      {isModalOpen && (
        <TransportationModal
          onCloseClick={handleCloseClick}
          deliveryMethods={deliveryMethods}
          breederFirstName={breederFirstName}
          facilityLocation={facilityLocation}
          puppyName={puppyName}
        />
      )}
    </div>
  );
}

Transport.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  deliveryMethods: PropTypes.object.isRequired,
  breederFirstName: PropTypes.string,
  facilityLocation: PropTypes.string,
  puppyName: PropTypes.string,
  distance: PropTypes.number
};

Transport.defaultProps = {
  breederFirstName: null,
  facilityLocation: null,
  puppyName: null,
  distance: null
};

function setOrigin({ analytics }) {
  const { origin_view: originView } = getAnalyticsOrigin();

  setAnalyticsOrigin({
    originView,
    originViewFeature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
    originViewFeatureLocation: analytics?.view_feature_location
  });
}

function getReviewsUrl(profileUrl) {
  const separator = profileUrl.includes('#') ? '&' : '#';

  return [profileUrl, 'tab=stories'].join(separator);
}

export default function Card({
  puppy,
  analytics,
  className,
  trackInView,
  onFavoriteChange
}) {
  const { locationTitle, breedId } = useSelector(({ server }) => server);
  const { isLoggedIn, isBuyer } = useCurrentUser();

  const {
    id,
    name,
    profile_photo_id: profilePhotoId,
    square_profile_photo_url: profilePhotoUrl,
    distance_in_miles: distance,
    breeder_profile: breederProfile,
    media_files: mediaFiles,
    approved_testimonial_count: reviewsCount,
    ready_for_adoption_date_v2: readyDateDescription
  } = puppy;

  const {
    id: breederProfileId,
    url: breederProfileUrl,
    name: breederProfileProgramName,
    display_name: breederProfileDisplayName,
    delivery_methods: deliveryMethods,
    first_name: breederFirstName,
    preferred_qualification: preferredQualification,
    profile_photo_url_wo_missing: breederPhotoUrl,
    response_time_description: responseTimeDescription
  } = breederProfile;

  const preferredQualificationStatus = preferredQualification?.status;
  const isPreferredBreeder = preferredQualificationStatus === STATUS.PREFERRED;

  const ageGenderDescription = [
    puppy.age_description,
    capitalize(puppy.gender || '')
  ]
    .filter(Boolean)
    .join(' • ');

  const hasReviews = reviewsCount > 0;

  const favoriteAnalytics = useMemo(
    () => ({
      ...analytics,
      page_number: analytics.page_number || null,
      position: analytics.position || null,
      breeder_profile_id: breederProfileId,
      breeder_name: breederProfileProgramName,
      dog_id: id,
      breed_id: Number(breedId) || null
    }),
    [id, analytics]
  );

  const computedAnalytics = useMemo(
    () => ({
      ...analytics,
      breeder_preferred_status: preferredQualificationStatus,
      breeder_profile_id: breederProfileId,
      view_feature: ANALYTICS_VIEW_FEATURES.PUPPY_CARD,
      miles_away: distance,
      dog_id: id,
      breed_id: breedId,
      transportation: flatten(Object.values(deliveryMethods || {})).map(
        method => method.name
      )
    }),
    [id, analytics]
  );

  const trackBreederLinkClick = useCallback(
    properties => {
      setOrigin({ analytics });
      sendRudderstackEvent(ANALYTICS_EVENTS.CARD_CLICKED_BY_USER, {
        ...computedAnalytics,
        ...properties
      });
    },
    [id, analytics]
  );

  const puppyMediaFiles = useMemo(() => {
    const pupMediaFiles = [...(mediaFiles || [])];
    if (profilePhotoId || pupMediaFiles.length === 0) {
      pupMediaFiles.unshift({
        id: profilePhotoId,
        url: profilePhotoUrl
      });
    }
    return pupMediaFiles;
  }, [mediaFiles, profilePhotoId]);

  const photosWithAlt = useMemo(() => {
    const images = puppyMediaFiles.map(photo => ({
      ...photo,
      alt: locationTitle ? `${name} in ${locationTitle}` : name
    }));

    return images;
  }, [locationTitle, name, puppyMediaFiles]);

  const userAvatarBreeder = {
    ...breederProfile,
    profile_photo_url: breederPhotoUrl
  };

  const handleCardClick = useCallback(
    ({ currentTarget, target }) => {
      if (currentTarget !== target && !currentTarget.contains(target)) {
        return;
      }

      setOrigin({ analytics });

      sendRudderstackEvent(
        ANALYTICS_EVENTS.CARD_CLICKED_BY_USER,
        computedAnalytics
      );

      openPuppyPage({ puppyId: id });
    },
    [analytics, computedAnalytics]
  );

  return (
    <InView
      triggerOnce
      onChange={inView => {
        if (inView) trackInView();
      }}
      threshold={0.8}
      className={classnames('PuppySearchResultsCard h-100 db', className)}
    >
      <div className="PuppySearchResultsCard__container h-100">
        <ClickableDiv className="w-100 relative z-0" onClick={handleCardClick}>
          <div className="relative flex flex-column justify-center br3 overflow-hidden">
            <CardMediaCarousel
              mediaFiles={photosWithAlt}
              smartImageProps={{
                Tag: 'div',
                className: 'w-100 bg-center cover',
                loadingClass: 'bg-light-gray',
                height: IS_LT_MD ? 'calc(100vw - 48px)' : '335px',
                allowContextMenu: true,
                crop: true
              }}
              lazyBuffer={window.outerHeight * 2}
              isPuppyCard
            />
            <div className="white absolute w-100 top-0 right-0">
              <div className="ph4 pv4 absolute right-0">
                {(!isLoggedIn || isBuyer) && (
                  <FavoriteButton
                    analytics={favoriteAnalytics}
                    followableTypeId={id}
                    followableType={FOLLOWABLE_TYPES.DOG}
                    onChange={onFavoriteChange}
                  />
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between gap-24 mt4">
            <p className="fw-medium truncate font-24 font-serif">
              {getPuppyName(puppy)}
            </p>
            {readyDateDescription && (
              <Badge className="nowrap" color="gray" size="small">
                {readyDateDescription}
              </Badge>
            )}
          </div>
          {ageGenderDescription && (
            <p className="font-14 secondary">{ageGenderDescription}</p>
          )}
        </ClickableDiv>
        <div>
          <div className="flex items-center gap-8 bt b--default mt3 pt2">
            <a
              className="relative"
              href={breederProfileUrl}
              target="_blank"
              onClick={() => {
                trackBreederLinkClick({
                  view_feature_location:
                    ANALYTICS_VIEW_FEATURE_LOCATIONS.BREEDER_PROFILE_ICON
                });
              }}
            >
              <UserAvatar user={userAvatarBreeder} />
              {isPreferredBreeder && (
                <PreferredBreederBadge
                  className="absolute right-0 bottom-0"
                  width="20px"
                  height="20px"
                  hideTooltip
                />
              )}
            </a>
            <div>
              <a
                className="font-16 truncate"
                href={breederProfileUrl}
                target="_blank"
                onClick={() =>
                  trackBreederLinkClick({
                    view_feature_location:
                      ANALYTICS_VIEW_FEATURE_LOCATIONS.BREEDER_PROFILE_NAME
                  })
                }
              >
                Raised by {breederProfileDisplayName}
              </a>
              <Transport
                className="font-12 tertiary"
                deliveryMethods={deliveryMethods}
                breederFirstName={breederFirstName}
                puppyName={getPuppyName(puppy)}
                distance={distance}
              />
            </div>
          </div>
          {(responseTimeDescription || hasReviews) && (
            <div className="flex flex-column gap-4 mt2 secondary">
              {responseTimeDescription && (
                <p className="flex items-center gap-4">
                  <Icon name="fetch-clock" height="20px" width="20px" />
                  <span className="font-14 lh-title">
                    {responseTimeDescription}
                  </span>
                </p>
              )}
              {hasReviews && (
                <a
                  className="flex items-center gap-4"
                  href={getReviewsUrl(breederProfileUrl)}
                  target="_blank"
                  onClick={() => {
                    trackBreederLinkClick({
                      view_feature_location:
                        ANALYTICS_VIEW_FEATURE_LOCATIONS.REVIEWS_LINK
                    });
                  }}
                >
                  <Icon name="fetch-review" height="20px" width="20px" />
                  <span className="font-14 lh-title underline">
                    {reviewsCount === 1
                      ? `${reviewsCount} review`
                      : `${reviewsCount} reviews`}
                  </span>
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </InView>
  );
}

Card.propTypes = PuppyCardPropTypes;
Card.defaultProps = PuppyCardPropDefaultTypes;
